import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, startWith } from 'rxjs';

import { ActionConfiguratorFormField } from '@blaze-shared/connectors-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  ALL_COLUMN_TYPES,
  BaseSchema,
  ColumnLike,
  DatasetColumn,
  findColumn,
  getArrayColumns,
  getColumnKey,
  isDataArray,
  PageContextSchema,
} from '@blaze-shared/schema-domain';
import {
  isBulkActionWorkflow,
  isDeleteNode,
  isGetNode,
  isMergeNode,
  isUpdateNode,
  LookupWorkflowNode,
  Workflow,
  WorkflowNode,
  WorkflowSchemas,
} from '@blaze-shared/workflow-domain';

import { IntelliConfig } from './models/intellisense';

@Injectable({ providedIn: 'root' })
export class IntelliConfigService {
  private readonly schemaService = inject(SchemaService);

  /**
   * Creates intellisense configuration for an assignment. Assignments don't support column references ({colId:xxxx})
   * so datasetSchema is not passed.
   * @param assignment
   * @returns
   */
  createConfigForAssignment(
    assignment: ActionConfiguratorFormField,
    node: WorkflowNode,
    workflow$: Observable<Workflow | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
    pageContextSchema$: Observable<PageContextSchema | null | undefined>,
    index: number,
  ): Observable<IntelliConfig> {
    return combineLatest({
      pageContextSchema: pageContextSchema$,
      schemas: schemas$.pipe(startWith(null)),
      workflow: workflow$.pipe(startWith(null)),
    }).pipe(
      map(({ pageContextSchema, schemas, workflow }) => {
        const nodeId = node.id;
        const columnIdOrName = assignment.name;
        const datasetSchema = schemas?.schema;
        const column =
          assignment.valueSchema?.columns?.[0] ||
          findColumn(datasetSchema?.columns, columnIdOrName);
        const namedInputsSchemas = this.getNamedInputsSchemas(workflow, schemas);
        const varsFromWorkflow = schemas?.variables || [];
        const configId = this.getConfigId(column, assignment.name || index);
        const baseInputSchema = schemas?.nodeInputSchemasMap.get(nodeId);
        let inputSchema: BaseSchema | undefined = baseInputSchema;
        if (isMergeNode(node) && baseInputSchema) {
          inputSchema = node.input ? namedInputsSchemas[node.input] : baseInputSchema;
          if (isDataArray(inputSchema.columns)) {
            inputSchema = { ...inputSchema, columns: getArrayColumns(inputSchema.columns) };
          } else if (inputSchema.columns.length === 0) {
            sentryLog(
              `Merge input schema is empty; workflowId: ${workflow?.id} nodeId: ${node.id}`,
              'warning',
            );
          } else {
            sentryLog(
              `Merge input is not an Array; workflowId: ${workflow?.id} nodeId: ${node.id}`,
              'warning',
            );
          }
        }

        const config: IntelliConfig = {
          id: configId,
          column,
          columnTypes: column ? [] : ALL_COLUMN_TYPES,
          fixedValuesSchema: schemas?.schema,
          inputSchema,
          namedInputsSchemas,
          nodeInputSchemasMap: schemas?.nodeInputSchemasMap,
          pageContextSchema,
          placeholder: 'empty value',
          varsFromWorkflow,
          valueInfo: {
            displayInfos: assignment.displayInfos,
            isFormulaValue: assignment.isFormulaValue,
            value: assignment.value,
          },
          workflow,
          workflowNode: node,
        };
        return config;
      }),
    );
  }

  /**
   * Creates intellisense configuration for a single query condition.
   * @param assignment
   * @returns
   */
  createConfigForColumn(
    column: DatasetColumn,
    node: WorkflowNode,
    workflow$: Observable<Workflow | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
    pageContextSchema$: Observable<PageContextSchema | null | undefined>,
    partialConfig: Partial<IntelliConfig> = {},
    idPrefix = '',
    ignoreDatasetSchema = false,
  ): Observable<IntelliConfig> {
    return combineLatest({
      pageContextSchema: pageContextSchema$,
      schemas: schemas$.pipe(startWith(null)),
      workflow: workflow$.pipe(startWith(null)),
    }).pipe(
      map(({ pageContextSchema, schemas, workflow }) => {
        const columnIdOrName = getColumnKey(column);
        const nodeId = node.id;
        const inputSchema = schemas?.nodeInputSchemasMap.get(nodeId);
        const datasetSchema = schemas?.schema;
        const namedInputsSchemas = this.getNamedInputsSchemas(workflow, schemas);
        const varsFromWorkflow = schemas?.variables || [];
        const configId = partialConfig?.id || this.getConfigId(column, columnIdOrName, idPrefix);

        const config: IntelliConfig = {
          ...partialConfig,
          column,
          columnTypes: column ? [] : ALL_COLUMN_TYPES,
          datasetSchema: ignoreDatasetSchema ? undefined : datasetSchema,
          disableDatasetColumns: !isGetNode(node), // dataset columns are only supported for Get node
          id: configId,
          inputSchema,
          namedInputsSchemas,
          nodeInputSchemasMap: schemas?.nodeInputSchemasMap,
          pageContextSchema,
          varsFromWorkflow,
          workflow,
          workflowNode: node,
        };
        return config;
      }),
    );
  }

  /**
   * Creates intellisense configuration for a single query condition.
   * @param assignment
   * @returns
   */
  createConfigForColumnIdOrName(
    columnIdOrName: string,
    node: WorkflowNode,
    workflow$: Observable<Workflow | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
    pageContextSchema$: Observable<PageContextSchema | null | undefined>,
    partialConfig: Partial<IntelliConfig> = {},
    idPrefix = '',
    ignoreDatasetSchema = false,
  ): Observable<IntelliConfig> {
    return combineLatest({
      pageContextSchema: pageContextSchema$,
      schemas: schemas$.pipe(startWith(null)),
      workflow: workflow$.pipe(startWith(null)),
    }).pipe(
      map(({ pageContextSchema, schemas, workflow }) => {
        const nodeId = node.id;
        const inputSchema = schemas?.nodeInputSchemasMap.get(nodeId);

        // Certain nodes such as Aggregate overwrite schema entirely. As such, schemas.schema may not be a proper schema
        // to consider for finding the proper column. We default to it only if the node we're working with is
        // is a Get node or a node which modifies specified dataset (BL-8701).
        let datasetSchema = schemas?.nodeInputSchemasMap.get(node.id);
        if ((isDeleteNode(node) || isGetNode(node) || isUpdateNode(node)) && node.datasetId) {
          datasetSchema = this.schemaService.getSchema(node.datasetId);
        } else if (!datasetSchema?.columns?.length) {
          datasetSchema = schemas?.schema;
        }

        const column = findColumn(datasetSchema?.columns, columnIdOrName);

        const namedInputsSchemas = this.getNamedInputsSchemas(workflow, schemas);
        const varsFromWorkflow = schemas?.variables || [];
        const configId = partialConfig?.id || this.getConfigId(column, columnIdOrName, idPrefix);

        const config: IntelliConfig = {
          ...partialConfig,
          column,
          columnTypes: column ? [] : ALL_COLUMN_TYPES,
          datasetSchema: ignoreDatasetSchema ? undefined : datasetSchema,
          id: configId,
          inputSchema,
          namedInputsSchemas,
          nodeInputSchemasMap: schemas?.nodeInputSchemasMap,
          pageContextSchema,
          varsFromWorkflow,
          workflow,
          workflowNode: node,
        };
        return config;
      }),
    );
  }

  /**
   * Create intellisense config for Join condition in lookup node.
   * @param columnIdOrName
   * @param node
   * @param schemas$
   * @param pageContextSchema$
   * @param partialConfig
   * @param idPrefix
   * @param ignoreDatasetSchema
   * @returns
   */
  createConfigForJoinCondition(
    columnIdOrName: string,
    node: LookupWorkflowNode,
    workflow$: Observable<Workflow | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
    pageContextSchema$: Observable<PageContextSchema | null | undefined>,
    partialConfig: Partial<IntelliConfig> = {},
  ): Observable<IntelliConfig> {
    return combineLatest({
      nodeSchema: this.schemaService.obtainSchema(node.datasetId),
      pageContextSchema: pageContextSchema$,
      schemas: schemas$.pipe(startWith(null)),
      workflow: workflow$,
    }).pipe(
      map(({ nodeSchema, pageContextSchema, schemas, workflow }) => {
        const sourceSchema = schemas?.nodeInputSchemasMap.get(node.id);
        const column = findColumn(nodeSchema?.columns, columnIdOrName);
        const varsFromWorkflow = schemas?.variables || [];
        const configId = partialConfig?.id || this.getConfigId(column, columnIdOrName, node.id);

        const config: IntelliConfig = {
          ...partialConfig,
          column,
          columnTypes: column ? [] : ALL_COLUMN_TYPES,
          disableFormulas: true,
          disableGlobalVars: true,
          id: configId,
          // Right side of the join condition (controlled by intellisense) MUST be from the input table.
          inputSchema: sourceSchema,
          nodeInputSchemasMap: schemas?.nodeInputSchemasMap,
          pageContextSchema,
          varsFromWorkflow,
          workflow,
          workflowNode: node,
        };
        return config;
      }),
    );
  }

  /**
   * Creates intellisense configuration for a single query condition.
   * @param assignment
   * @returns
   */
  createConfigForNodeInput(
    id: string,
    node: WorkflowNode,
    workflow$: Observable<Workflow | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
    pageContextSchema$: Observable<PageContextSchema | null | undefined>,
    partialConfig: Partial<IntelliConfig> = {},
    disableNodeInputSchema = false,
  ): Observable<IntelliConfig> {
    return combineLatest({
      pageContextSchema: pageContextSchema$,
      schemas: schemas$.pipe(startWith(null)),
      workflow: workflow$.pipe(startWith(null)),
    }).pipe(
      map(({ pageContextSchema, schemas, workflow }) => {
        const nodeId = node.id;
        const inputSchema = disableNodeInputSchema
          ? undefined
          : schemas?.nodeInputSchemasMap.get(nodeId);
        const namedInputsSchemas = this.getNamedInputsSchemas(workflow, schemas);
        const varsFromWorkflow = schemas?.variables || [];

        // Default to all column types if neither the column nor column types are specified.
        const columnTypes =
          !partialConfig?.column && !partialConfig?.columnTypes?.length
            ? ALL_COLUMN_TYPES
            : partialConfig.columnTypes;
        const config: IntelliConfig = {
          ...partialConfig,
          columnTypes,
          disableDatasetColumns: !isGetNode(node), // dataset columns are only supported for Get node
          id,
          inputSchema,
          namedInputsSchemas,
          nodeInputSchemasMap: schemas?.nodeInputSchemasMap,
          pageContextSchema,
          varsFromWorkflow,
          workflow,
          workflowNode: node,
        };
        return config;
      }),
    );
  }

  getConfigId(
    column: Partial<ColumnLike> | string | undefined | null,
    nameOrIndex: number | string = '',
    idPrefix = '',
  ): string {
    return `${idPrefix}${getColumnKey(column) || nameOrIndex || ''}`;
  }

  private getNamedInputsSchemas(
    workflow?: Workflow | null,
    schemas?: WorkflowSchemas | null,
  ): { [k: string]: BaseSchema } {
    // Bulk action workflows do not use any of the named schemas.
    if (!!workflow && isBulkActionWorkflow(workflow)) {
      return {};
    }
    return schemas?.namedInputsSchemas ? Object.fromEntries(schemas?.namedInputsSchemas) : {};
  }
}
